import {
createContext,
useContext,
useEffect,
useMemo,
useState,
ReactNode,
FC
} from "react";

interface EnvContextValue {
  isProduction: boolean;
  isEmulatedProduction: boolean;
  toggleProductionEmulation: () => void;
}

interface EnvProviderProps {
  children: ReactNode;
}

const EnvContext = createContext<EnvContextValue | undefined>(undefined);
const EMULATE_PRODUCTION_KEY = "MemoryOSemulProd" as const;

export const useEnvContext = (): EnvContextValue => {
  const context = useContext(EnvContext);
  if (!context) {
    throw new Error("useEnvContext must be used within EnvProvider");
  }
  return context;
};

const getInitialEmulationState = (): boolean => {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(EMULATE_PRODUCTION_KEY) === "true";
};

const checkIsRealProduction = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const currentURL = window.location.href;
  const productionDomain = "start.memoryos.com";
  const stagingDomain = "staging.start.memoryos.com";

  return (
    currentURL.includes(productionDomain) &&
    !currentURL.includes(stagingDomain)
  );
};

export const EnvProvider: FC<EnvProviderProps> = ({ children }) => {
  const [emulateProduction, setEmulateProduction] = useState<boolean>(getInitialEmulationState);

  const isRealProduction = useMemo(() => checkIsRealProduction(), []);

  const isProduction = isRealProduction || emulateProduction;

  const toggleProductionEmulation = (): void => {
    const newState = !emulateProduction;

    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(EMULATE_PRODUCTION_KEY, String(newState));
    }

    setEmulateProduction(newState);

    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  useEffect(() => {
    if (isProduction) {
      console.log(
        "%cMemoryOS production" + (emulateProduction ? " (emulated)" : ""),
        "color:rgb(0, 196, 0); font-weight: bold;"
      );
    } else {
      console.log(
        "%cMemoryOS staging",
        "color:rgb(173, 113, 1); font-weight: bold;"
      );
    }
  }, [isProduction, emulateProduction]);

  const contextValue: EnvContextValue = {
    isProduction,
    isEmulatedProduction: emulateProduction,
    toggleProductionEmulation,
  };

  return (
    <EnvContext.Provider value={contextValue}>{children}</EnvContext.Provider>
  );
};